import React from 'react'
import { ToggleGroup } from '../ToggleGroup'
import { offsetPTInput } from '../../utils/timezone'
import formStyles from '../../styles/forms.module.scss'
import styles from './ScheduleFields.module.scss'

interface ScheduleFieldsProps {
  /** Start bound as a datetime-local value in Pacific Time; '' means "now". */
  startsAt: string
  onStartsAtChange: (value: string) => void
  /** End bound, same encoding; '' means "no end date". */
  endsAt: string
  onEndsAtChange: (value: string) => void
  /** Whether the end input is showing. Held by the caller because a placement
   * keeps the field open while it is momentarily blank, where a hero slide
   * treats blank as "no end date" outright. */
  hasEnd: boolean
  onHasEndChange: (hasEnd: boolean) => void
  /** Chips offered under the start input — the placement timeline's change
   * points. A hero slide has no equivalent, so it passes nothing. */
  startSuggestions?: React.ReactNode
  /** Days ahead the default start/end land on when a bound is switched on. */
  startDefaultDays?: number
  endDefaultDays?: number
}

/**
 * The start/end pair that schedules something: a widget placement, or one hero
 * slide. Both bounds are optional, both are authored in Pacific Time, and the
 * window they describe is half-open — which is how ACE reads a placement's
 * starts_at/ends_at and a hero slide's alike, so one editor can serve both.
 *
 * Values are datetime-local strings, not Dates: that is what the input speaks,
 * and it keeps the caller in charge of the UTC conversion at its own storage
 * boundary (convertPTInputToUTC / convertUTCToPTInput).
 */
export const ScheduleFields: React.FC<ScheduleFieldsProps> = ({
  startsAt,
  onStartsAtChange,
  endsAt,
  onEndsAtChange,
  hasEnd,
  onHasEndChange,
  startSuggestions,
  startDefaultDays = 1,
  endDefaultDays = 14,
}) => (
  <>
    <div className={formStyles.fieldGroup}>
      <label>Starts</label>
      <ToggleGroup
        variant="toggle"
        size="small"
        options={[
          { value: 'now', label: 'Now' },
          { value: 'scheduled', label: 'Scheduled' },
        ]}
        value={startsAt ? 'scheduled' : 'now'}
        onChange={(v) => {
          if (v === 'now') {
            onStartsAtChange('')
          } else if (!startsAt) {
            onStartsAtChange(offsetPTInput(null, startDefaultDays))
          }
        }}
      />
      {startsAt && (
        <>
          <div className={styles.dateInputRow}>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => onStartsAtChange(e.target.value)}
              className={styles.dateInput}
            />
            <span className={styles.tzLabel}>Pacific Time</span>
          </div>
          {startSuggestions}
        </>
      )}
    </div>

    <div className={formStyles.fieldGroup}>
      <label>Ends</label>
      <ToggleGroup
        variant="toggle"
        size="small"
        options={[
          { value: 'none', label: 'No end date' },
          { value: 'date', label: 'End date' },
        ]}
        value={hasEnd ? 'date' : 'none'}
        onChange={(v) => {
          if (v === 'none') {
            onHasEndChange(false)
            onEndsAtChange('')
          } else {
            onHasEndChange(true)
            if (!endsAt) onEndsAtChange(offsetPTInput(startsAt || null, endDefaultDays))
          }
        }}
      />
      {hasEnd && (
        <div className={styles.dateInputRow}>
          <input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => onEndsAtChange(e.target.value)}
            className={styles.dateInput}
          />
          <span className={styles.tzLabel}>Pacific Time</span>
        </div>
      )}
    </div>
  </>
)
